/**
 * <region-notice> — the bottom bar shown to visitors outside the countries a
 * product is sold in.
 *
 * Whether this visitor is outside those countries was already decided on the
 * server, so this only handles when the bar appears and when it stops appearing:
 *
 * - The delay is a reading delay, not a timer from page load. A visitor who
 *   arrives and immediately leaves for another tab shouldn't come back to a
 *   popup they never saw open, so the countdown pauses while the page is hidden.
 * - Dismissal is remembered per browser in localStorage. Storage can throw
 *   outright (private windows, blocked site data), so every access is guarded and
 *   the bar simply behaves as if nothing was remembered.
 * - The timezone check is a second opinion on the country. Shopify's market
 *   detection can put an Australian visitor in another market — a VPN, a
 *   manually switched country — and showing them "not available in your region"
 *   for a product they can buy is the worst failure available here.
 */
(() => {
  class RegionNotice extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-notice-panel]');
      this.closeButton = this.querySelector('[data-notice-close]');
      if (!this.panel) return;

      this.designMode = this.hasAttribute('data-design-mode');
      this.storageKey = this.dataset.storageKey || 'region-notice';

      // The editor needs to see it immediately, every time, with no memory of
      // having been closed.
      if (this.designMode) {
        this.#open();
        this.#wireClose();
        return;
      }

      if (this.#inSkippedTimezone() || this.#alreadyDismissed()) return;

      this.#wireClose();
      this.#countdown(Number(this.dataset.delay || 0) * 1000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.onKeydown) document.removeEventListener('keydown', this.onKeydown);
    }

    /* Counts down only while the page is actually being looked at. */
    #countdown(remaining) {
      if (remaining <= 0) {
        this.#open();
        return;
      }

      let startedAt = Date.now();
      let left = remaining;

      const resume = () => {
        startedAt = Date.now();
        this.timer = setTimeout(() => this.#open(), left);
      };

      this.onVisibility = () => {
        if (document.hidden) {
          clearTimeout(this.timer);
          left -= Date.now() - startedAt;
        } else if (left > 0) {
          resume();
        }
      };

      document.addEventListener('visibilitychange', this.onVisibility);
      if (!document.hidden) resume();
    }

    #open() {
      this.panel.hidden = false;
      // Reading a layout property commits the off-screen start state synchronously,
      // so the class change that follows has something to transition from. Done
      // with rAF instead, a throttled tab can leave the bar un-hidden but still
      // translated off screen — visible to nobody and clickable by nobody.
      void this.panel.offsetHeight;
      this.panel.classList.add('is-open');
      // The scrim fades with the panel, so the state lives on the host too.
      this.classList.add('is-open');

      this.onKeydown = (event) => {
        if (event.key === 'Escape') this.#dismiss();
      };
      document.addEventListener('keydown', this.onKeydown);
    }

    #wireClose() {
      this.closeButton?.addEventListener('click', () => this.#dismiss());
    }

    #dismiss() {
      this.panel.classList.remove('is-open');
      this.classList.remove('is-open');
      // Kept out of the layout once it has finished sliding away.
      setTimeout(() => {
        this.panel.hidden = true;
      }, 400);

      if (this.onKeydown) {
        document.removeEventListener('keydown', this.onKeydown);
        this.onKeydown = null;
      }

      if (this.designMode || !this.hasAttribute('data-remember')) return;
      try {
        localStorage.setItem(this.storageKey, '1');
      } catch (error) {
        // Nothing to do: the bar shows again next visit, which is the safe way
        // to fail for a notice the visitor has already read.
      }
    }

    #alreadyDismissed() {
      if (!this.hasAttribute('data-remember')) return false;
      try {
        return localStorage.getItem(this.storageKey) === '1';
      } catch (error) {
        return false;
      }
    }

    #inSkippedTimezone() {
      const prefix = this.dataset.skipTimezone;
      if (!prefix) return false;
      try {
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        return zone.startsWith(prefix);
      } catch (error) {
        return false;
      }
    }
  }

  if (!customElements.get('region-notice')) {
    customElements.define('region-notice', RegionNotice);
  }
})();
